import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, RefreshCw, Home, Search } from 'lucide-react';
import { CLS_BTN_PRIMARY, CLS_BTN_SECONDARY } from '../constants';
import { Seo } from './Seo';
import { trackEvent } from '../services/tracking';

interface GlobalErrorFallbackProps {
    error: Error;
    resetErrorBoundary: () => void;
}

export const GlobalErrorFallback: React.FC<GlobalErrorFallbackProps> = ({ error, resetErrorBoundary }) => {
    const { t } = useTranslation('common');

    // Runtime-error beacon (4th audit P0: crashes were invisible — HTTP said
    // 200 while users saw this page). Feeds D1 `events`, so the error rate per
    // route is measurable and "technici boli informovaní" is actually true.
    useEffect(() => {
        try {
            trackEvent('runtime_error', {
                path: window.location.pathname,
                message: String(error?.message || error?.name || 'unknown').slice(0, 300),
            });
        } catch { /* beacon must never crash the fallback */ }
    }, [error]);

    useEffect(() => {
        const isChunkLoadFailed =
            error?.message?.includes('Failed to fetch dynamically imported module') ||
            error?.message?.includes('Failed to load chunk') ||
            error?.name === 'ChunkLoadError' ||
            /Failed to fetch.*module/i.test(error?.message || '');

        if (isChunkLoadFailed) {
            console.warn('[ErrorBoundary] ChunkLoadError detected. Reloading page to get latest assets...');
            
            const lastReload = sessionStorage.getItem('last_chunk_reload');
            const now = Date.now();
            // Allow reload only once every 15 seconds to prevent infinite reload loops if offline
            if (!lastReload || now - parseInt(lastReload) > 15000) {
                sessionStorage.setItem('last_chunk_reload', String(now));
                window.location.reload();
            }
        }
    }, [error]);

    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4">
            <Seo title={t('error.global_title', 'Niečo sa pokazilo')} noindex />
            <div className="max-w-md w-full bg-white dark:bg-stone-900 rounded-2xl shadow-xl border border-stone-100 dark:border-stone-800 p-8 text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mx-auto mb-6">
                    <AlertCircle size={32} />
                </div>

                <h1 className="text-2xl font-black text-stone-900 dark:text-white mb-2">
                    {t('error.global_title', 'Niečo sa pokazilo')}
                </h1>

                <p className="text-stone-500 dark:text-stone-400 mb-6 text-sm">
                    {t('error.global_description', 'Ospravedlňujeme sa, ale aplikácia narazila na nečakanú chybu. Naši technici boli o probléme informovaní.')}
                </p>

                {import.meta.env.DEV && (
                    <div className="text-left bg-stone-100 dark:bg-stone-950 p-4 rounded-xl mb-6 overflow-x-auto">
                        <p className="font-mono text-xs text-red-600 dark:text-red-400 break-all">
                            {error.message}
                        </p>
                    </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                    <button
                        onClick={resetErrorBoundary}
                        className={`${CLS_BTN_PRIMARY} flex-1 flex items-center justify-center gap-2`}
                    >
                        <RefreshCw size={18} />
                        {t('error.try_again', 'Skúsiť znova')}
                    </button>
                    <a
                        href="/market/"
                        className={`${CLS_BTN_SECONDARY} flex-1 flex items-center justify-center gap-2`}
                    >
                        <Search size={18} />
                        {t('error.browse_market', 'Prezrieť priestory')}
                    </a>
                    <a
                        href="/"
                        className={`${CLS_BTN_SECONDARY} flex-1 flex items-center justify-center gap-2`}
                        onClick={() => {
                            // Clear potential bad state in hash/history
                            window.location.hash = '';
                        }}
                    >
                        <Home size={18} />
                        {t('error.go_home', 'Domov')}
                    </a>
                </div>
            </div>
        </div>
    );
};
